import React, { useState, useEffect } from 'react';
import { FiX, FiSettings } from 'react-icons/fi';

const CURRENCIES = ['USD', 'EUR', 'TRY', 'AED', 'SAR', 'GBP', 'JPY', 'CNY', 'INR'];

const BoardFinanceSettingsModal = ({ isOpen, board, token, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    baseCurrency: 'USD',
    supervisionFeePercent: 0,
    financialSummaryFinalized: false,
    shareSubtasksInClientPortal: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && board) {
      setFormData({
        baseCurrency: board.baseCurrency || 'USD',
        supervisionFeePercent: board.supervisionFeePercent ?? 0,
        financialSummaryFinalized: !!board.financialSummaryFinalized,
        shareSubtasksInClientPortal: !!board.shareSubtasksInClientPortal,
      });
    }
  }, [isOpen, board]);

  if (!isOpen || !board) return null;

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards/${board.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baseCurrency: formData.baseCurrency,
          supervisionFeePercent: parseFloat(formData.supervisionFeePercent) || 0,
          // PART K: Board supervision is ALWAYS visible. Hidden mode is removed.
          supervisionFeeVisible: true,
          financialSummaryFinalized: !!formData.financialSummaryFinalized,
          shareSubtasksInClientPortal: !!formData.shareSubtasksInClientPortal,
        }),
      });
      if (res.ok) {
        onUpdate && onUpdate();
        onClose();
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2 className="modal-title"><FiSettings size={18} style={{ verticalAlign: 'middle' }} /> Client Statement Settings</h2>
          <button className="modal-close" onClick={onClose}><FiX size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Project Base Currency</label>
            <select
              className="input"
              value={formData.baseCurrency}
              onChange={(e) => setFormData({ ...formData, baseCurrency: e.target.value })}
              data-testid="settings-base-currency"
            >
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <p className="form-helper">All finance totals will display in this currency.</p>
          </div>

          <div className="form-group">
            <label className="form-label">Supervision Fee (%)</label>
            <input
              type="number" step="0.1" min="0" max="100"
              className="input"
              value={formData.supervisionFeePercent}
              onChange={(e) => setFormData({ ...formData, supervisionFeePercent: e.target.value })}
              data-testid="settings-supervision-fee"
            />
            <p className="form-helper">
              Percentage added on top of all client-visible task amounts. Always shown as a
              transparent line on the Client Statement. Set to <strong>0</strong> for no supervision.
            </p>
            <div style={{ background: 'var(--gray-50)', padding: 12, borderRadius: 8, marginTop: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
              <strong>Transparent supervision:</strong> Client sees each task at its client-visible amount,
              then a separate "Supervision Fee ({formData.supervisionFeePercent || 0}%)" line added to the total.
              <em> No hidden supervision mode is supported on the Board — all hidden profit lives in Quotations & Costing.</em>
            </div>
          </div>

          {/* PART J: financialSummaryFinalized */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.financialSummaryFinalized}
                onChange={(e) => setFormData({ ...formData, financialSummaryFinalized: e.target.checked })}
                data-testid="settings-financial-finalized"
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <span>Mark financial summary as finalized</span>
            </label>
            <div style={{
              background: formData.financialSummaryFinalized ? 'rgba(29, 191, 115, 0.08)' : 'var(--gray-50)',
              padding: 12, borderRadius: 8, marginTop: 8, fontSize: 13, color: 'var(--text-secondary)'
            }}>
              {formData.financialSummaryFinalized ? (
                <>
                  <strong>Finalized:</strong> Client Portal shows the project total without an
                  "incomplete pricing" warning. Use this only after every client-visible item has a price.
                </>
              ) : (
                <>
                  <strong>Not finalized:</strong> If any client-visible item lacks a price, the
                  Client Portal will warn the client with: <em>"Summary includes priced items only…"</em>
                </>
              )}
            </div>
          </div>

          {/* Share subtasks with client (Client Portal) */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.shareSubtasksInClientPortal}
                onChange={(e) => setFormData({ ...formData, shareSubtasksInClientPortal: e.target.checked })}
                data-testid="settings-share-subtasks"
                style={{ width: 18, height: 18, cursor: 'pointer' }}
              />
              <span>Share subtasks with the client</span>
            </label>
            <div style={{
              background: formData.shareSubtasksInClientPortal ? 'rgba(0, 115, 234, 0.08)' : 'var(--gray-50)',
              padding: 12, borderRadius: 8, marginTop: 8, fontSize: 13, color: 'var(--text-secondary)'
            }}>
              {formData.shareSubtasksInClientPortal ? (
                <>
                  <strong>Enabled:</strong> Each task in the Client Portal will show its subtasks
                  (name, status, progress). Internal fields like assignees are never shared.
                </>
              ) : (
                <>
                  <strong>Disabled:</strong> Subtasks stay internal. The Client Portal only shows
                  the parent tasks. Toggle on when you want the client to see task-by-task detail.
                </>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading} data-testid="settings-save-btn">
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoardFinanceSettingsModal;
